import { useEffect, useRef } from "react";

/**
 * Hook custom: safety realign dei prezzi del piano di cura al listino
 * dell'assicurazione attiva.
 *
 * Estratto da App.js il 25/02/2026 (137 righe del useEffect originale).
 *
 * Logica complessa testata in produzione: pagine come Chiama Paziente
 * possono aprire un'opp PRIMA che il listino corretto sia stato
 * caricato in `treatments`, esponendo i prezzi BDD invece di quelli
 * assicurazione. Questo hook:
 *
 *   1. **Race-condition guard** (17/06/2026): se il listino in memoria
 *      è di un'altra assicurazione, salta del tutto (aspetta il
 *      prossimo run con i `treatments` corretti).
 *   2. **Fallback guard**: se `treatmentsMeta.fallbackUsed=true` E
 *      l'assicurazione attiva non è Base/BDD, salta (la UI mostra già
 *      un banner).
 *   3. **Auto-heal "firma del bug"** (24/06/2026): in edit-mode, se
 *      una riga ha `euro` ≈ prezzo BDD ma il listino assicurazione ha
 *      un valore diverso → corregge silenziosamente (l'opp è stata
 *      creata durante la race-condition mobile pre-safety-net).
 *   4. **Guard edit-mode first-load**: il DB è source-of-truth, non
 *      sovrascriviamo finché l'utente non tocca un dropdown.
 *   5. **Combo-key marker**: ignora re-fetch della stessa coppia
 *      (assicurazione|reparto|treatments.length).
 *
 * @returns `userTouchedListinoRef` — da settare a `true` quando
 *   l'utente cambia assicurazione o reparto via dropdown, così il
 *   guard #4 viene by-passato e il realign parte.
 */
export function usePianoCuraPriceRealign({
  treatments,
  treatmentsMeta,
  stageLockedInfo,
  pianoCura,
  setPianoCura,
  editingOpportunityId,
  assicurazione,
  reparto,
  insurances,
  baseCosts,
}) {
  // Marker per evitare loop di realign sulla stessa combo dati.
  const realignedForComboRef = useRef("");
  // True dopo che l'utente ha esplicitamente cambiato assicurazione o
  // reparto (sblocca il realign in edit-mode).
  const userTouchedListinoRef = useRef(false);

  // Resetta i marker quando cambia l'opp in edit (riapertura della stessa
  // o di un'altra opp).
  useEffect(() => {
    userTouchedListinoRef.current = false;
    realignedForComboRef.current = "";
  }, [editingOpportunityId]);

  useEffect(() => {
    if (!treatments || treatments.length === 0) return;
    if (stageLockedInfo) return; // piano in sola lettura
    if (!pianoCura || pianoCura.length === 0) return;

    // Guard 0 (17/06/2026): RACE CONDITION GUARD — se il listino caricato
    // in memoria appartiene a un'assicurazione DIVERSA da quella dell'opp
    // corrente (es. l'utente è arrivato qui aprendo prima una scheda con
    // BDD/Base, poi un'altra con UniSalute), non possiamo fidarci di
    // `priceMap` né di `baseCosts` finché `useAppData.loadTreatments`
    // non ha ri-caricato per la NUOVA assicurazione. Saltiamo del tutto
    // questa esecuzione: il piano in DB resta visibile (€ corretti),
    // e il prossimo run dell'useEffect (dopo che `treatments` si aggiorna)
    // farà partire la logica normale.
    // Bug originale: al primo open di una scheda, il realign girava col
    // listino STALE precedente → display sovrascritto con prezzi BDD
    // → dopo qualche secondo i `treatments` si aggiornavano e il display
    // tornava corretto. Adesso l'esecuzione viene proprio saltata.
    const currentInsName = (insurances.find((i) => i.id === assicurazione)?.name || "").trim();
    const queriedInsName = (treatmentsMeta?.insuranceQueried || "").trim();
    // Confronto normalizzato: il backend ritorna a volte il nome esatto,
    // a volte la chiave del foglio. Normalizziamo (lowercase, no prefisso
    // numerico) per evitare falsi mismatch su "5 - UniSalute" vs "UniSalute".
    const _normIns = (s) => (s || "").toLowerCase().replace(/^\d+\s*[-–]\s*/, "").trim();
    if (currentInsName && queriedInsName && _normIns(currentInsName) !== _normIns(queriedInsName)) {
      console.debug(
        `piano_cura realign: SKIPPED (listino in memoria '${queriedInsName}' ≠ assicurazione attiva '${currentInsName}')`
      );
      return;
    }
    // Guard 1: fallback silenzioso del listino → non sovrascrivere i
    // prezzi salvati. Il banner mostrato dalla UI (`insuranceMismatch`)
    // chiede già all'utente di correggere; intanto teniamo intatti i
    // prezzi del piano salvato.
    if (treatmentsMeta?.fallbackUsed) {
      const insName = (insurances.find((i) => i.id === assicurazione)?.name || "").trim();
      const norm = insName.toLowerCase().replace(/^\d+\s*[-–]\s*/, "").trim();
      const isExplicitBaseBdd = (norm === "base" || norm === "bdd");
      if (!isExplicitBaseBdd) {
        console.debug("piano_cura realign: SKIPPED (fallback silenzioso a Base)");
        return;
      }
    }
    // Build code → cost map case-insensitive (codici nei fogli Google
    // hanno casing inconsistente: "ABT" vs "abf" vs "1EC"). Escludiamo
    // le baseExtraTreatments BDD-only (`isBaseOnly:true`) per non
    // sovrascrivere il prezzo Welion con quello BDD per codici che il
    // backend ha aggiunto come "extra BDD" nei reparti Indiretti.
    const priceMap = new Map();
    for (const t of treatments) {
      if (t?.isBaseOnly) continue;
      const code = (t?.code || "").toUpperCase();
      if (code) priceMap.set(code, Number(t.cost) || 0);
    }
    // Bug-signature audit (24/06/2026): SELF-HEALING per dati legacy
    // che hanno la "firma del bug" (prezzo salvato = listino BDD ≠
    // listino assicurazione attivo) — tipicamente opp create durante la
    // race condition mobile pre-safety-net. `baseCosts` arriva dal
    // backend con i prezzi BDD per i codici condivisi (vedi
    // routes_treatments.py: base_overlay). Eseguiamo questa pass
    // PRIMA di Guard 2, così anche in edit-mode il display si
    // auto-corregge senza bisogno di un'azione utente.
    const bdd = baseCosts || {};
    const _TOL = 0.005;
    if (editingOpportunityId && !userTouchedListinoRef.current && Object.keys(bdd).length > 0) {
      let fixedCount = 0;
      const healed = pianoCura.map((g) => {
        const trts = (g.trattamenti || []).map((tr) => {
          if (!tr.treatmentCode) return tr;
          if (tr.priceChoice === "bdd" || tr.priceChoice === "manual") return tr;
          if (tr.indirettoTarget) return tr;
          const codeU = String(tr.treatmentCode).toUpperCase();
          const insCost = priceMap.get(codeU);
          const bddCost = bdd[codeU];
          if (insCost == null || bddCost == null) return tr;
          const saved = Number(tr.euro) || 0;
          // Firma del bug: prezzo salvato ≈ BDD ma ≠ assicurazione.
          if (Math.abs(saved - bddCost) < _TOL && Math.abs(saved - insCost) >= _TOL) {
            fixedCount += 1;
            return { ...tr, euro: insCost };
          }
          return tr;
        });
        return { ...g, trattamenti: trts };
      });
      if (fixedCount > 0) {
        console.warn(
          `piano_cura: AUTO-HEALED ${fixedCount} righe con firma del bug listino BDD → assicurazione`,
        );
        setPianoCura(healed);
        realignedForComboRef.current = `${assicurazione}|${reparto}|${treatments.length}`;
        return; // setState triggera un nuovo passaggio del useEffect
      }
    }
    // Guard 2: in edit-mode, NON ri-allineare al primo carico dei
    // treatments — il piano salvato è la fonte di verità. Si attiva
    // solo dopo un'azione manuale dell'utente (cambio
    // assicurazione/reparto via dropdown).
    if (editingOpportunityId && !userTouchedListinoRef.current) {
      console.debug("piano_cura realign: SKIPPED in edit mode (first load, trust DB)");
      // Marca questo combo come già "visto" così se l'utente preme
      // poi un dropdown la prima azione viene gestita come change.
      realignedForComboRef.current = `${assicurazione}|${reparto}|${treatments.length}`;
      return;
    }
    // Guard 3: combo-key marker → ignora re-fetch della stessa coppia.
    const comboKey = `${assicurazione}|${reparto}|${treatments.length}`;
    if (realignedForComboRef.current === comboKey) return;
    realignedForComboRef.current = comboKey;
    let changed = false;
    let alignedCount = 0;
    const next = pianoCura.map((g) => {
      const trts = (g.trattamenti || []).map((tr) => {
        if (!tr.treatmentCode) return tr;
        // Skip realign quando l'utente ha esplicitamente scelto un
        // listino diverso per questa riga (caso Indiretti dual-price)
        // o ha editato il prezzo a mano. Senza questa guard il realign
        // resetterebbe la scelta utente al cambio combo.
        // Skip anche per le righe `indirettoTarget` valorizzato: il loro
        // prezzo segue il listino BDD/Indiretti, non quello assicurativo
        // dell'opp corrente.
        if (tr.priceChoice === "bdd" || tr.priceChoice === "manual") return tr;
        if (tr.indirettoTarget) return tr;
        const codeU = String(tr.treatmentCode).toUpperCase();
        if (!priceMap.has(codeU)) return tr;
        const newCost = priceMap.get(codeU);
        if (Math.abs((Number(tr.euro) || 0) - newCost) < 0.005) return tr;
        changed = true;
        alignedCount += 1;
        return { ...tr, euro: newCost };
      });
      return { ...g, trattamenti: trts };
    });
    if (changed) {
      console.debug(`piano_cura: realigned ${alignedCount} prices to current listino (combo=${comboKey})`);
      setPianoCura(next);
    }
  }, [treatments, treatmentsMeta, stageLockedInfo, pianoCura, editingOpportunityId, assicurazione, reparto, insurances, baseCosts, setPianoCura]);

  return { userTouchedListinoRef };
}
